import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, CheckCircle, XCircle } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_4aa3e30f-645a-43e8-83ed-b83576a14c9f/artifacts/p43xkrnl_SusTek%20Colored%20Logo%20Name%20-%20Horizontal.svg";

const InviteAccess = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading"); // loading, valid, invalid, used
  const [inviteData, setInviteData] = useState(null);

  useEffect(() => {
    const validateToken = async () => {
      try {
        const response = await axios.get(`${API}/invites/${token}`);
        setInviteData(response.data);
        setStatus("valid");
      } catch (error) {
        if (error.response?.status === 400) {
          setStatus("used");
        } else {
          setStatus("invalid");
        }
      }
    };

    if (token) {
      validateToken();
    }
  }, [token]);

  const handleContinue = () => {
    navigate(`/auth?invite=${token}`);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-cream to-white flex flex-col">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50" data-testid="invite-header">
        <div className="container-main py-4 flex items-center">
          <Link to="/" className="flex items-center gap-2" data-testid="invite-logo">
            <img src={LOGO_HORIZONTAL} alt="SusTek" className="h-10" />
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center py-12 px-4">
        <Card className="w-full max-w-md border-border/50 shadow-lg animate-fade-in" data-testid="invite-card">
          <CardHeader className="text-center">
            <CardTitle className="font-heading text-2xl text-foreground">
              Supplier Invitation
            </CardTitle>
            <CardDescription>
              ESG Questionnaire Access
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {status === "loading" && (
              <div className="text-center py-8" data-testid="invite-loading">
                <Loader2 className="w-12 h-12 text-forest animate-spin mx-auto mb-4" />
                <p className="text-muted-foreground">Validating your invitation...</p>
              </div>
            )}

            {status === "valid" && (
              <div className="text-center py-4" data-testid="invite-valid">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                  Invitation Valid!
                </h3>
                <p className="text-muted-foreground mb-4">
                  You've been invited to complete the ESG questionnaire.
                </p>
                {inviteData?.company_name && (
                  <p className="text-sm text-foreground bg-cream p-3 rounded-lg mb-4">
                    <span className="font-medium">Company:</span> {inviteData.company_name}
                  </p>
                )}
                {inviteData?.email && (
                  <p className="text-sm text-foreground bg-cream p-3 rounded-lg mb-4">
                    <span className="font-medium">Email:</span> {inviteData.email}
                  </p>
                )}
                <Button 
                  onClick={handleContinue}
                  className="w-full btn-primary"
                  data-testid="invite-continue-btn"
                >
                  Continue to Registration
                </Button>
              </div>
            )}

            {status === "invalid" && (
              <div className="text-center py-4" data-testid="invite-invalid">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                  Invalid Invitation
                </h3>
                <p className="text-muted-foreground mb-4">
                  This invitation link is invalid or has expired.
                </p>
                <Link to="/auth">
                  <Button className="w-full btn-primary" data-testid="invite-login-btn">
                    Go to Login
                  </Button>
                </Link>
              </div>
            )}

            {status === "used" && (
              <div className="text-center py-4" data-testid="invite-used">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-foreground mb-2">
                  Invitation Already Used
                </h3>
                <p className="text-muted-foreground mb-4">
                  This invitation has already been used. Please login to continue.
                </p>
                <Link to="/auth">
                  <Button className="w-full btn-primary" data-testid="invite-login-existing-btn">
                    Go to Login
                  </Button>
                </Link>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="py-6 border-t border-border bg-cream">
        <div className="container-main text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} SusTek Sustainable Supply Chain Portal. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default InviteAccess;
